import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, debounceTime, map, of, switchMap } from 'rxjs'
import { AuditsService } from '../../../infrastructure/audits.service'
import { MS_BEFORE_RESET_LOADING_STATE } from '../audits.constants'
import { updateAuditActions } from './update-audit.actions'
import { toUpdateAuditRequest } from '../../../adapter/audits/to-update-audit.request'
import { toUpdateAuditChargesRequest } from '../../../adapter/audits/to-update-audit-charges.request'
import { toAddAuditChargesMappingsRequest } from '../../../adapter/audits/to-add-audit-charges-mappings.request'
import { toUpdateAuditChargesV2Request } from '../../../adapter/audits/to-update-audit-charges-v2.request'
import { Audit } from '../../../domain/audits/audit.model'
import { platform_v2 } from '@navix/api-models'
import { platform_v2_1 } from '@navix/services-api-models'
import { fromGetAuditResponse } from '../../../adapter/audits/FromGetAuditResponse'

export const updateAudit = createEffect(
  (actions$ = inject(Actions), service = inject(AuditsService)) => {
    return actions$.pipe(
      ofType(updateAuditActions.updateAudit),

      switchMap(payload =>
        service.getAuditByOrderId(payload.orderUuid).pipe(
          switchMap(response =>
            service
              .updateAudit(
                payload.orderUuid,
                toUpdateAuditRequest(payload, response),
                payload.vendorInvoiceId
              )
              .pipe(
                map(() => updateAuditActions.updateAuditSuccess()),
                catchError(error => {
                  let message = error.error?.message || error.message
                  if (error.message && error.message.status === 403) {
                    message = error.message.error.message
                  }
                  return of(
                    updateAuditActions.updateAuditFail({ error: message })
                  )
                })
              )
          ),
          catchError(error => of(updateAuditActions.updateAuditFail({ error })))
        )
      )
    )
  },
  {
    functional: true
  }
)

export const updateAuditCharges = createEffect(
  (actions$ = inject(Actions), service = inject(AuditsService)) => {
    return actions$.pipe(
      ofType(updateAuditActions.updateAuditCharges),
      switchMap(payload =>
        service.getAuditByOrderId(payload.orderUuid).pipe(
          switchMap(response => {
            const request = toUpdateAuditChargesRequest(payload, response)
            if (request === false)
              return of(
                updateAuditActions.updateAuditChargesFail({
                  error:
                    'We have detected an issue in the vendor charges.  Please refresh the browser and try again.',
                  isMissingOrderAuditDocument: false
                })
              )

            return service
              .updateAudit(payload.orderUuid, request, payload.vendorInvoiceId)
              .pipe(
                map(() => updateAuditActions.updateAuditChargesSuccess()),
                catchError(error =>
                  of(
                    updateAuditActions.updateAuditChargesFail({
                      error,
                      isMissingOrderAuditDocument: false
                    })
                  )
                )
              )
          }),
          catchError(error => {
            return of(
              updateAuditActions.updateAuditChargesFail({
                error,
                isMissingOrderAuditDocument: error.status === 404
              })
            )
          })
        )
      )
    )
  },
  {
    functional: true
  }
)

export const updateAuditChargesMappings = createEffect(
  (actions$ = inject(Actions), service = inject(AuditsService)) => {
    return actions$.pipe(
      ofType(updateAuditActions.addAuditChargesMappings),
      switchMap(payload =>
        service.getAuditByOrderId(payload.orderUuid).pipe(
          switchMap(response =>
            service
              .updateAudit(
                payload.orderUuid,
                toAddAuditChargesMappingsRequest(payload, response),
                payload.vendorInvoiceId
              )
              .pipe(
                map(() => updateAuditActions.addAuditChargesMappingsSuccess()),
                catchError(error => {
                  let message = error.error?.message || error.message
                  if (error.message && error.status === 403) {
                    message = error.error.message
                  }
                  console.log(message)
                  console.log(error)
                  return of(
                    updateAuditActions.addAuditChargesMappingsFail({
                      error: message
                    })
                  )
                })
              )
          )
        )
      )
    )
  },
  {
    functional: true
  }
)

export const resetUpdateAuditChargesMappings = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.addAuditChargesMappingsSuccess,
        updateAuditActions.addAuditChargesMappingsFail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetAddAuditChargesMappingsLoadingState())
    )
  },
  {
    functional: true
  }
)

export const resetUpdateAudit = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.updateAuditSuccess,
        updateAuditActions.updateAuditFail,
        updateAuditActions.updateAuditChargesSuccess,
        updateAuditActions.updateAuditChargesFail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetUpdateAuditLoadingState())
    )
  },
  {
    functional: true
  }
)

export const updateAuditChargesV2 = createEffect(
  (actions$ = inject(Actions), service = inject(AuditsService)) => {
    return actions$.pipe(
      ofType(updateAuditActions.updateAuditChargesV2),
      switchMap(payload =>
        service.getAuditByOrderId(payload.orderUuid).pipe(
          switchMap(response =>
            service
              .updateAuditChargesV2(
                payload.auditUuid,
                payload.invoiceUuid,
                toUpdateAuditChargesV2Request(payload, response)
              )
              .pipe(
                map(() => updateAuditActions.updateAuditChargesV2Success()),
                catchError(error => {
                  let message = error.error?.message || error.message
                  if (error.message && error.status === 403) {
                    message = error.error.message
                  } else if (error.message && error.status === 409) {
                    message = error.error.message
                  }
                  return of(
                    updateAuditActions.updateAuditChargesV2Fail({
                      error: message,
                      isMissingOrderAuditDocument: error.status === 404,
                      isMissingVendorInvoiceCharges: error.status === 409
                    })
                  )
                })
              )
          ),
          catchError(error => {
            return of(
              updateAuditActions.updateAuditChargesFail({
                error,
                isMissingOrderAuditDocument: error.status === 404
              })
            )
          })
        )
      )
    )
  },
  {
    functional: true
  }
)

export const resetUpdateAuditChargesV2 = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.updateAuditChargesV2Success,
        updateAuditActions.updateAuditChargesV2Fail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetupdateAuditChargesV2LoadingState())
    )
  },
  {
    functional: true
  }
)

// TODO update this effect so you don't need to pass in the VendorId
export const updateAuditVendor = createEffect(
  (actions$ = inject(Actions), service = inject(AuditsService)) => {
    return actions$.pipe(
      ofType(updateAuditActions.updateAuditVendor),
      switchMap(payload =>
        service.getAuditByOrderId(payload.orderUuid).pipe(
          switchMap(response =>
            service
              .updateAuditVendor(payload.auditUuid, payload.invoiceUuid, {
                vendorExternalId: payload.vendorExternalId,
                auditDocumentEtag: response.eTag ?? ''
              })
              .pipe(
                map((updateVendorResponse: Audit) => {
                  return updateAuditActions.updateAuditVendorSuccess({
                    newVendorId:
                      updateVendorResponse.vendors[payload.vendorExternalId]
                        .vendorId
                  })
                }),
                catchError(error => {
                  let message = error.error?.message || error.message
                  if (error.message && error.status === 403) {
                    message = error.error.message
                  }
                  return of(
                    updateAuditActions.updateAuditVendorFail({
                      error: message,
                      isMissingOrderAuditDocument: error.status === 404
                    })
                  )
                })
              )
          ),
          catchError(error => {
            return of(
              updateAuditActions.updateAuditChargesFail({
                error,
                isMissingOrderAuditDocument: error.status === 404
              })
            )
          })
        )
      )
    )
  },
  {
    functional: true
  }
)

export const resetUpdateAuditVendor = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.updateAuditVendorSuccess,
        updateAuditActions.updateAuditVendorFail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetUpdateAuditVendorLoadingState())
    )
  },
  {
    functional: true
  }
)

export const updateAuditOwnership = createEffect(
  (
    actions$ = inject(Actions),
    platformV21 = inject(platform_v2_1.UpdateAuditOwnershipService),
    platformV2 = inject(platform_v2.AuditService)
  ) => {
    return actions$.pipe(
      ofType(updateAuditActions.updateAuditOwnership),
      switchMap(payload =>
        platformV2
          .getApiVAuditsOrder({ orderUuid: payload.orderUuid, version: '2' })
          .pipe(
            switchMap(response =>
              platformV21
                .putApiV21AuditsOwnership({
                  auditUuid: payload.auditUuid,
                  requestBody: {
                    ownInvoice: payload.isOwned,
                    auditDocumentEtag: response.eTag ?? ''
                  }
                })
                .pipe(
                  map(() => {
                    return updateAuditActions.updateAuditOwnershipSuccess()
                  }),
                  catchError(error => {
                    let message = error.error?.message || error.message
                    if (error.message && error.status === 403) {
                      message = error.error.message
                    }
                    return of(
                      updateAuditActions.updateAuditOwnershipFail({
                        error: message,
                        isMissingOrderAuditDocument: error.status === 404
                      })
                    )
                  })
                )
            )
          )
      )
    )
  },
  {
    functional: true
  }
)

export const resetUpdateAuditOwnership = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.updateAuditOwnershipSuccess,
        updateAuditActions.updateAuditOwnershipFail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetUpdateAuditOwnershipLoadingState())
    )
  },
  {
    functional: true
  }
)

export const approveOrderFromAudit = createEffect(
  (
    actions$ = inject(Actions),
    platformV21 = inject(platform_v2_1.ApproveOrderService)
  ) => {
    return actions$.pipe(
      ofType(updateAuditActions.approveOrderFromAudit),
      switchMap(({ auditETag, auditUuid }) =>
        platformV21
          .putApiV21AuditsApprove({
            auditUuid: auditUuid,
            requestBody: {
              auditDocumentEtag: auditETag
            }
          })
          .pipe(
            map(() => {
              return updateAuditActions.approveOrderFromAuditSuccess()
            }),
            catchError(error => {
              let message = error.error?.message || error.message
              if (error.message && error.status === 403) {
                message = error.error.message
              }
              return of(
                updateAuditActions.approveOrderFromAuditFail({
                  error: message,
                  isMissingOrderAuditDocument: error.status === 404
                })
              )
            })
          )
      )
    )
  },
  {
    functional: true
  }
)

export const resetApproveOrderFromAudit = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        updateAuditActions.approveOrderFromAuditSuccess,
        updateAuditActions.approveOrderFromAuditFail
      ),
      debounceTime(MS_BEFORE_RESET_LOADING_STATE),
      map(() => updateAuditActions.resetApproveOrderFromAuditLoadingState())
    )
  },
  {
    functional: true
  }
)

//#region reaudit order
export const reAuditOrder = createEffect(
  (
    actions$ = inject(Actions),
    preBillService = inject(platform_v2_1.PreBillService)
  ) =>
    actions$.pipe(
      ofType(updateAuditActions.reAuditOrder),
      switchMap(({ orderUuid }) =>
        preBillService
          .runPreBillRules({
            orderUuid: orderUuid
          })
          .pipe(
            map(response =>
              updateAuditActions.reAuditOrderSuccess({
                audit: fromGetAuditResponse(response)
              })
            ),
            catchError(error => {
              console.error('There was an error trying to reaudit order', error)
              return of(
                updateAuditActions.reAuditOrderFailed({
                  error
                })
              )
            })
          )
      )
    ),
  {
    functional: true
  }
)

export const resetAuditOrderLoadingState = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        updateAuditActions.reAuditOrderSuccess,
        updateAuditActions.reAuditOrderFailed
      ),
      debounceTime(100),
      map(() => updateAuditActions.resetReAuditOrderLoadingState())
    ),
  {
    functional: true
  }
)
//#endregion

//#region attest work instructions
export const attestWorkInstructions = createEffect(
  (
    actions$ = inject(Actions),
    preBillService = inject(platform_v2_1.AttestWorkInstructionsService)
  ) =>
    actions$.pipe(
      ofType(updateAuditActions.attestWorkInstructions),
      switchMap(({ auditUuid, auditETag, workInstructionUuids }) =>
        preBillService
          .putApiV21AuditsWorkInstructionsAttestations({
            auditUuid,
            requestBody: {
              auditDocumentEtag: auditETag,
              workInstructionUuids
            }
          })
          .pipe(
            map(response =>
              updateAuditActions.attestWorkInstructionsSuccess({
                audit: fromGetAuditResponse(response)
              })
            ),
            catchError(error => {
              console.error(
                'There was an error trying to attest work instructions',
                error
              )
              return of(
                updateAuditActions.attestWorkInstructionsFailed({
                  error
                })
              )
            })
          )
      )
    ),
  {
    functional: true
  }
)

export const resetAttestWorkInstructionsLoadingState = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        updateAuditActions.attestWorkInstructionsSuccess,
        updateAuditActions.attestWorkInstructionsFailed
      ),
      debounceTime(100),
      map(() => updateAuditActions.resetAttestWorkInstructionsLoadingState())
    ),
  {
    functional: true
  }
)
//#endregion
